import { t } from "adapters/i18n";
import type { HerdRecord } from "shared/types";
import type { DataResult, ServiceConstructor } from "../services.types";

export function HerdRecordsServiceMixin<TBase extends ServiceConstructor>(
  Base: TBase
) {
  return class HerdRecordsService extends Base {
    readonly herdRecordsUrl = `${process.env.REACT_APP_ENDPOINT}herds/herdRecords`;

    /**
     * @remarks This endpoint uses the farmId from the header instead of the query param
     */
    async listFarmRecords(): Promise<DataResult<HerdRecord>> {
      try {
        const result = await this.get(this.herdRecordsUrl);

        if (result.data?.data == null) {
          return { data: [], total: 0 };
        }

        const data = result.data.data as HerdRecord[];
        const total = result.data.total as number;

        return { data, total };
      } catch (_) {
        throw new Error(t("movements:fetch-herd-records-error"));
      }
    }

    async createRecord(
      payload: Pick<
        HerdRecord,
        | "animalCategoryId"
        | "carcassYield"
        | "confirmed"
        | "farmId"
        | "operationDate"
        | "operationTypeId"
        | "quantity"
        | "recordTypeId"
        | "weight"
      >
    ): Promise<HerdRecord> {
      try {
        const result = await this.post(this.herdRecordsUrl, payload);

        const data = result.data.data;
        return data;
      } catch (_) {
        throw new Error(t("movements:create-herd-record-error"));
      }
    }

    async deleteRecord(recordId: number): Promise<void> {
      try {
        await this.delete(`${this.herdRecordsUrl}/${recordId}`);
      } catch (_) {
        throw new Error(t("movements:delete-herd-record-error"));
      }
    }

    async updateRecord(
      recordId: number,
      payload: Partial<
        Pick<
          HerdRecord,
          | "animalCategoryId"
          | "carcassYield"
          | "confirmed"
          | "farmId"
          | "operationDate"
          | "operationTypeId"
          | "quantity"
          | "recordTypeId"
          | "weight"
        >
      >
    ): Promise<HerdRecord> {
      try {
        const result = await this.patch(
          `${this.herdRecordsUrl}/${recordId}`,
          payload
        );

        return result.data.data as HerdRecord;
      } catch (_) {
        throw new Error(t("movements:update-herd-record-error"));
      }
    }
  };
}
